<template>
  <div style="display: flex; flex-direction: column; height: 100%">
    <v-fade-transition>
      <div
        v-if="cuedSceneTemplate"
        style="
          height: 231px;
          width: 100%;
          border-radius: 4px;
          overflow: hidden;
          display: flex;
          background: rgb(var(--v-theme-cardColorAltAlt));
          gap: 4px;
          position: relative;
          flex-shrink: 0; /* Important: Prevents this from shrinking */
        "
        class="pa-2 mb-2">
        <div
          style="
            width: 384px;
            height: 216px;
            overflow: hidden;
            position: relative;
            border-radius: 5px;
          ">
          <PreviewStream
            :frameNo="props.frameNo"
            :instanceKey="previewStreamKey"
            @request-reset="onPreviewReset" />
        </div>
        <div
          class="cue-controls"
          style="
            flex-grow: 1;
            display: flex;
            align-items: end;
            justify-content: center;
            flex-direction: column;
            gap: 8px;
          ">
          <v-card-subtitle>Cue Controls</v-card-subtitle>
          <v-spacer />
          <div style="display: flex; gap: 8px">
            <v-btn
              @click="previewControl('left')"
              size="small"
              variant="tonal">
              <v-icon size="x-large">mdi-arrow-left-bold</v-icon>
            </v-btn>
            <v-btn
              @click="previewControl('right')"
              size="small"
              variant="tonal">
              <v-icon size="x-large">mdi-arrow-right-bold</v-icon>
            </v-btn>
          </div>
          <div style="display: flex; gap: 8px">
            <v-btn
              @click="previewControl('up')"
              size="small"
              variant="tonal">
              <v-icon size="x-large">mdi-arrow-up-bold</v-icon>
            </v-btn>
            <v-btn
              @click="previewControl('down')"
              size="small"
              variant="tonal">
              <v-icon size="x-large">mdi-arrow-down-bold</v-icon>
            </v-btn>
          </div>
          <div style="display: flex; gap: 8px">
            <v-btn
              @click="previewControl('zoom_in')"
              size="small"
              variant="tonal">
              <v-icon size="x-large">mdi-magnify-plus</v-icon>
            </v-btn>
            <v-btn
              @click="previewControl('zoom_out')"
              size="small"
              variant="tonal">
              <v-icon size="x-large">mdi-magnify-minus</v-icon>
            </v-btn>
          </div>
          <div style="display: flex; gap: 8px">
            <ResetPreviewButton
              :title="resetMessage"
              :disabled="
                !deluge.new_cued_dids?.[frameNo] || cuedSceneTemplate?.multi
              "
              :channelUUID="currentChannel?.uid" />
            <AnimatePreviewButton
              :title="startPreviewMessage"
              :disabled="!deluge.new_cued_dids?.[frameNo]"
              :channelUUID="currentChannel?.uid" />
          </div>
        </div>
      </div>
    </v-fade-transition>

    <div
      style="
        flex-grow: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
      ">
      <v-tabs v-model="cueAndTakeTab">
        <v-tab>Presets</v-tab>
        <v-tab>This Channel</v-tab>
      </v-tabs>

      <v-window
        v-model="cueAndTakeTab"
        style="flex-grow: 1; overflow-y: auto">
        <v-window-item
          :value="0"
          style="height: 100%">
          <Templates
            :showTags="false"
            filter="all"
            :height="'100%'"
            :selectMode="true"
            :dense="true"
            :selectMultiple="false"
            return-object
            @selected="selectTemplate" />
        </v-window-item>
        <v-window-item
          :value="1"
          style="height: 100%">
          <div
            :onClick="() => cueScene(did)"
            v-for="(did, i) in didsInChannel"
            :key="(did, i)">
            <WideCard
              :loading="false"
              :icon="'mdi-cube-outline'"
              :img="findFirstAsset(deluge.items?.[did])"
              :title="deluge.items?.[did].name"
              :selected="did === deluge.new_cued_dids?.[frameNo]"
              :icons="[]"
              :check="deluge.new_cued_dids?.[frameNo]"
              :dense="true"></WideCard>
          </div>
        </v-window-item>
      </v-window>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineAsyncComponent } from 'vue';
import { $http } from '../../../stores/http.js';
import { deluge } from '@/stores/deluge.js';
import WideCard from '../../Shared/WideCard.vue';
import { getThumbnailForScene } from '@/utils/thumbnails.js';
import PreviewStream from '../PreviewStream.vue';
import AnimatePreviewButton from '../CueTake/AnimatePreviewButton.vue';
import ResetPreviewButton from '../CueTake/ResetPreviewButton.vue';

// Use dynamic import to avoid bundling Templates into main chunk
const Templates = defineAsyncComponent(
  () => import('../../../components/Templates/Templates.vue')
);

const props = defineProps({
  frameNo: {
    type: [String, Number],
    required: true,
  },
  currentChannel: {
    type: Object,
    default: null,
  },
});

const previewStreamKey = ref(0);

// Prevent thrashing: rate-limit how often the parent will bump the key
// in response to child reset requests.
const PREVIEW_RESET_COOLDOWN = 3000;
let _lastPreviewReset = 0;
function onPreviewReset() {
  const now = Date.now();
  if (now - _lastPreviewReset < PREVIEW_RESET_COOLDOWN) return;
  _lastPreviewReset = now;
  previewStreamKey.value++;
}

const cueAndTakeTab = ref(0);

const cuedSceneTemplate = computed(() => {
  const cuedDid = deluge.new_cued_dids?.[props.frameNo];
  const scene = deluge.items[cuedDid];
  if (!cuedDid || !scene) return undefined;
  return deluge.templates[scene.scene_name];
});

const didsInChannel = computed(() => {
  return props.currentChannel?.queues?.flatMap((queue) =>
    queue.items.map((item) => item.did)
  );
});

const resetMessage = computed(() => {
  if (!deluge.new_cued_dids?.[props.frameNo])
    return 'Reset Disabled: No Scene is Cued';
  if (cuedSceneTemplate.value?.multi)
    return 'Reset Disabled: Only Scenes without a CanvasPro managed transition can be Reset';
  return 'Resets the cued scene to match live if they match';
});

const startPreviewMessage = computed(() => {
  if (!deluge.new_cued_dids?.[props.frameNo])
    return 'Start Disabled: No Scene is Cued';
  return 'Preview the Take';
});
async function selectTemplate(template) {
  const templateUid = template[0].uid;
  const triggerFormat = {
    triggers: [
      {
        name: 'Start Preset',
        type: 'start_preset',
        vars: [
          {
            hint: 'Starts a specific preset, returns to original after playback',
            name: 'Start Preset',
            type: 'preset_select',
            value: { name: '', value: templateUid },
          },
          {
            hint: '',
            name: 'Frames',
            options: 'frames',
            type: 'autocomplete',
            value: [props.frameNo],
          },
          {
            hint: '',
            name: 'AlwaysCue',
            type: 'bool',
            value: true,
          },
        ],
      },
    ],
  };
  await $http(
    'POST',
    `/dynamic_engine/run_trigger`,
    JSON.stringify(triggerFormat)
  );
}

function previewControl(action) {
  $http(
    'PUT',
    `/dynamic_engine/preview_control?action=${action}&frame_id=${props.frameNo}`
  );
}

async function cueScene(did) {
  await $http('POST', `/dynamic_engine/cue/${did}`);
}

function findFirstAsset(scene) {
  const r = getThumbnailForScene(scene, deluge);
  return r.src;
}
</script>
